"use client";
import { FootNoteType, getFootNote } from "@/actions/get-foot-note";
import { useQuranOptions } from "@/app/providers/QuranContextProvider";
import useTranslations from "@/lib/hooks/useTranslations";
import { QuranResponseType, TranslationsListType } from "@/lib/types";
import { useEffect, useMemo, useRef, useState } from "react";
import { CircleX } from "lucide-react";
import { Button } from "./button";
import { AnimatePresence, motion } from "framer-motion";
import { Spinner } from "./spinner";
import { transLanguages } from "@/lib/helpers/functions";
import { cn } from "@/lib/helpers/utils";
import {
  indoPak,
  interFont,
  NastaleeqHafs,
  notoNaskhArabic,
  NotoNastaliq,
  notoSans,
  notoSansArabic,
  notoSansBeng,
  notoSansDeva,
  notoSansEth,
  notoSansGujr,
  notoSansGurm,
  notoSansHebrew,
  notoSansJP,
  notoSansKann,
  notoSansKhmr,
  notoSansKR,
  notoSansLao,
  notoSansMlym,
  notoSansOriy,
  notoSansSC,
  notoSansSinh,
  notoSansTaml,
  notoSansTel,
  notoSansThaa,
  notoSansThai,
} from "@/app/fonts";
import { NextFont } from "next/dist/compiled/@next/font";

export default function QuranTranslations({
  surah,
  verse,
  page,
  verseId,
}: {
  surah: number;
  verse: number;
  page: number;
  verseId: number;
}) {
  const { activeTranslations } = useQuranOptions();
  const selectedTrans = useMemo(
    () => (
      <>
        {activeTranslations.map((translation) => {
          return (
            <QuranTranstion
              key={`${translation.id}-${surah}-${verse}`}
              translationId={translation.id}
              surah={surah}
              verse={verse}
              page={page}
              verseId={verseId}
            />
          );
        })}
      </>
    ),
    [activeTranslations, page, surah, verse],
  );

  const trans = useMemo(
    () => (
      <div className="flex flex-col justify-between">
        {activeTranslations.length > 1 && (
          <div className="text-xs font-bold text-primary/80 pb-1">
            Translation(s)
          </div>
        )}
        {selectedTrans}
      </div>
    ),
    [selectedTrans],
  );

  return <>{trans}</>;
}

const QuranTranstion = ({
  surah,
  verse,
  page,
  verseId,
  translationId,
}: {
  translationId: number;
  surah: number;
  page: number;
  verse: number;
  verseId: number;
}) => {
  const { isTranslationLoaded, setIsTranslationLoaded } = useTranslations(
    translationId,
    page,
  );
  const { data, quranMushaf, activeTranslations } = useQuranOptions();

  const rtlLanguages = [
    "hebrew",
    "persian",
    "pashto",
    "sindhi",
    "uighur, uyghur",
    "kurdish",
    "urdu",
    "arabic",
  ];
  const fontMap: Record<string, NextFont> = {
    english: interFont,
    french: notoSans,
    spanish: notoSans,
    hebrew: notoSansHebrew,
    persian: notoSansArabic,
    kurdish: notoSansArabic,
    pashto: notoNaskhArabic,
    sindhi: NastaleeqHafs,
    uyghur: notoSansArabic,
    arabic: notoNaskhArabic,
    urdu: NotoNastaliq,
    chinese: notoSansSC,
    japanese: notoSansJP,
    korean: notoSansKR,
    hindi: notoSansDeva,
    bengali: notoSansBeng,
    gujarati: notoSansGujr,
    kannada: notoSansKann,
    malayalam: notoSansMlym,
    tamil: notoSansTaml,
    telugu: notoSansTel,
    punjabi: notoSansGurm,
    oriya: notoSansOriy,
    sinhala: notoSansSinh,
    khmer: notoSansKhmr,
    thai: notoSansThai,
    lao: notoSansLao,
    divehi: notoSansThaa,
    amharic: notoSansEth,
    bambara: notoSansEth,
    oromo: notoSansEth,
    tajik: notoSans,
    russian: notoSans,
    ukrainian: notoSans,
    bulgarian: notoSans,
    polish: notoSans,
    romanian: notoSans,
    bosnian: notoSans,
  };

  // const trans = useRef({ translation: "", name: "" });
  const translationRef = useRef<
    { translation: string; name: string } | undefined
  >(undefined);

  const getTranslation = useMemo(() => {
    let index = 0;
    let pge: QuranResponseType[] | undefined = undefined;

    if (quranMushaf.type == "indopak15" || quranMushaf.type == "indopak16") {
      if (quranMushaf.lines == 15 || quranMushaf.lines == 16)
        pge = data.current.IndoPak[quranMushaf.lines][page];
    } else pge = data.current.Uthmani[quranMushaf.type][page];

    if (pge) {
      for (; index < pge.length; index++) {
        if (pge[index].surah == surah && pge[index].verse == verse) {
          break;
        }
      }
    }

    if (quranMushaf.type == "indopak15" || quranMushaf.type == "indopak16") {
      if (quranMushaf.lines == 15 || quranMushaf.lines == 16) {
        if (
          data.current.IndoPak[quranMushaf.lines][page][index] &&
          data.current.IndoPak[quranMushaf.lines][page][index].translations
        )
          return data.current.IndoPak[quranMushaf.lines][page][index]
            .translations[translationId];
        return undefined;
      }
    } else {
      if (
        data.current.Uthmani[quranMushaf.type][page][index] &&
        data.current.Uthmani[quranMushaf.type][page][index].translations
      )
        return data.current.Uthmani[quranMushaf.type][page][index].translations[
          translationId
        ];
      return undefined;
    }
  }, [
    data,
    page,
    surah,
    verse,
    quranMushaf,
    translationId,
    isTranslationLoaded,
  ]);

  translationRef.current = getTranslation;
  useEffect(() => {
    if (!translationRef.current) {
      setIsTranslationLoaded(false);
    }
  }, []);

  let language = "";
  let name = "";
  let font = interFont.className;
  for (var i = 0; i < activeTranslations.length; i++) {
    if (activeTranslations[i].id == translationId) {
      name = activeTranslations[i].name;
      language = activeTranslations[i].language;
    }
  }
  return (
    <>
      {!translationRef.current ? (
        <div>
          <div className="text-text">
            <div className="animate-pulse rounded-md bg-muted h-9"></div>
          </div>
          <div className="animate-pulse rounded-md bg-muted h-2 w-[8rem] mt-1"></div>
        </div>
      ) : (
        <div
          className={cn("pb-4")}
          style={{
            direction: rtlLanguages.includes(language.toLowerCase())
              ? "rtl"
              : "ltr",
          }}
        >
          <div
            className={cn(
              "text-text",
              fontMap[language.toLowerCase()]
                ? fontMap[language.toLowerCase()].className
                : "",
            )}
          >
            <QuranVerseTranslation
              verseTranslationHtml={translationRef.current.translation}
            />
          </div>
          <div className="text-xs text-primary/80 pt-2">—— {name}</div>
        </div>
      )}
    </>
  );
};

function QuranVerseTranslation({
  verseTranslationHtml,
}: {
  verseTranslationHtml: string;
}) {
  const verseRef = useRef<HTMLDivElement | null>(null);
  const [footnote, setFootnote] = useState<FootNoteType>({
    id: 0,
    foot_note: "",
  });

  const handleClose = () => {
    setFootnote({
      id: 0,
      foot_note: "",
    });
  };

  useEffect(() => {
    const handleClick = async (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      if (
        target &&
        target.tagName === "SUP" &&
        target.hasAttribute("foot_note")
      ) {
        const footnoteId = target.getAttribute("foot_note");
        setFootnote({ id: 0, foot_note: "..Loading.." });

        if (footnoteId) {
          const resp = await fetch(`/api/get-footnote?footnote=${footnoteId}`);
          const result = await resp.json();
          if (result.success) {
            setFootnote(result.result);
          }
        }
      }
    };

    const node = verseRef.current;
    if (node) {
      node.addEventListener("click", handleClick);
    }

    return () => {
      if (node) {
        node.removeEventListener("click", handleClick);
      }
    };
  }, []);

  return (
    <div>
      <div
        className="leading-relaxed"
        ref={verseRef}
        dangerouslySetInnerHTML={{ __html: verseTranslationHtml }}
      />
      <AnimatePresence>
        {footnote.foot_note !== "" && (
          <motion.div
            layoutId={footnote.id.toString()}
            initial={{ height: 0 }}
            animate={{ height: "auto" }}
            transition={{ duration: 0.5 }}
            exit={{ height: 0 }}
            className="overflow-hidden"
          >
            {footnote.foot_note === "..Loading.." ? (
              <div className="h-30 text-center flex items-center justify-center">
                <Spinner className="text-primary" size={"large"}>
                  <span>Loading......</span>
                </Spinner>
              </div>
            ) : (
              <div className="bg-primary/10 p-3 rounded-md m-2">
                <Button
                  variant={"bgGhost"}
                  size={"icon"}
                  className="float-right"
                  onClick={handleClose}
                >
                  <CircleX className="size-10" />
                </Button>
                <div
                  className="p-4"
                  dangerouslySetInnerHTML={{
                    __html: footnote.foot_note.replace(
                      "indopak",
                      indoPak.className,
                    ),
                  }}
                ></div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
